import { TemplateElement, html, defineElement, when } from '@webtides/element-js';
import style from './e-banking-bento.css';

/**
 * @typedef {Object} BaseCardProperties
 * @property {string} color
 * @property {string} kicker
 * @property {string} title
 * @property {string} text
 * @property {ImageProperties} image
 */

/**
 * @typedef {Object} SquareCardProperties
 * @property {string} color
 * @property {string} text
 * @property {ImageProperties} image
 */

/**
 * @property {string} headline
 * @property {BaseCardProperties} landscapeCard
 * @property {SquareCardProperties} squareCard1
 * @property {SquareCardProperties} squareCard2
 */
export default class Bento extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            headline: '',
            landscapeCard: null,
            squareCard1: null,
            squareCard2: null,
        };
    }

    get squareCards() {
        const squareCards = [];

        if (this.squareCard1) {
            squareCards.push(this.squareCard1);
        }

        if (this.squareCard2) {
            squareCards.push(this.squareCard2);
        }

        return squareCards;
    }

    template() {
        return html`
            ${when(
                this.headline,
                html` <h2 class="text-xl font-medium text-green-medium mb-24">${this.headline}</h2> `,
            )}
            <div class="grid grid-cols-2 grid-rows-2 gap-fix-s">
                ${this.squareCards.map((card, index) => {
                    const hasText = card.text;
                    const hasKicker = card.kicker;
                    const hasHeadline = card.headline;
                    return html`
                        <div
                            class="h-full rounded-[var(--mdx-sys-border-radius-lg)] flex flex-col items-center justify-center text-center col-span-1 aspect-square w-full ${when(
                                card.color === 'light',
                                'bg-primary-subtle1 text-primary',
                                'bg-primary text-primary-on-default',
                            )}"
                        >
                            ${when(
                                hasKicker,
                                html`<p class="text-base shrink-0 content-end mb-0 px-fix-s">${card.kicker}</p>`,
                            )}
                            ${when(
                                hasHeadline,
                                html`<h2 class="shrink-0 content-end mb-0 px-fix-s">${card.headline}</h2>`,
                            )}
                            ${when(
                                hasText,
                                html`<p class="text-sm shrink-0 content-end mb-0 px-fix-s pt-16">${card.text}</p>`,
                            )}
                        </div>
                    `;
                })}
                <div
                    class="aspect-auto col-span-2 rounded-[var(--mdx-sys-border-radius-lg)] ${when(
                        this.landscapeCard.color === 'light',
                        'bg-primary-subtle1 text-primary',
                        'bg-primary text-primary-on-default',
                    )}"
                >
                    <div class="flex flex-row items-stretch gap-fix-2xl h-full">
                        <div class="self-center pl-fix-m py-fix-m flex-grow-1">
                            ${when(
                                this.landscapeCard.kicker,
                                html` <p class="text-base mb-0 mt-0">${this.landscapeCard.kicker}</p> `,
                            )}
                            ${when(
                                this.landscapeCard.title,
                                html`
                                    <h2 class="flex font-medium mb-0 mt-fix-2xs text-balance">
                                        ${this.landscapeCard.title}
                                    </h2>
                                `,
                            )}
                            ${when(
                                this.landscapeCard.text,
                                html`
                                    <p class="text-sm font-normal mb-0 mt-fix-2xs text-balance">
                                        ${this.landscapeCard.text}
                                    </p>
                                `,
                            )}
                        </div>
                        ${when(
                            this.landscapeCard.image?.src,
                            html`
                                <div class="aspect-auto !w-6/12 h-full">
                                    <img
                                        loading="lazy"
                                        src="${this.landscapeCard.image?.src}"
                                        srcset="${this.landscapeCard.image?.srcset}"
                                        alt="${this.landscapeCard.image?.alt}"
                                        class="h-full w-full object-cover rounded-r-lg rounded-l-none"
                                    />
                                </div>
                            `,
                        )}
                    </div>
                </div>
            </div>
        `;
    }
}

defineElement('mb-e-banking-bento', Bento);
